
import type { DailyByCategoryData } from '@empathybroker/tsclient-stats/dist/models/daily-by-category/daily-by-category-data'
import type { DailyByCategoryDistData } from '@empathybroker/tsclient-stats/dist/models/daily-by-category/daily-by-category-dist-data'
import type { TimelySearchDistributionData } from '@empathybroker/tsclient-stats/dist/models/timely-search/timely-search-distribution-data'
import type {
  EvolutionChartConfig,
  EvolutionChartData,
  Params,
  ValueConfig,
} from '@empathyco/insights'

import type { PropType } from 'vue'
import type { CategoriesTreeItem, RegularFilterSelectedValues } from '../../../models'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { EvolutionChartComponent } from '@empathyco/insights'

import { defineComponent } from 'vue'

import { KpiEnum } from '../../../enums'

import { InsightsMixin } from '../../../mixins'

import KpisLegend from '../../kpis-legend.component.vue'

export default defineComponent({
  name: 'OpportunityPerformanceEvolution',
  components: {
    KpisLegend,
  },
  mixins: [InsightsMixin],
  props: {
    selectedId: {
      type: String,
      required: true,
    },
    supportedKpis: {
      type: Array as PropType<Array<KpiEnum>>,
      default: () => [KpiEnum.QUERIES],
    },
    filters: Object as PropType<Record<string, RegularFilterSelectedValues>>,
    category: Object as PropType<CategoriesTreeItem>,
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-opportunity-performance-evolution',
      selectedKpis: [],
      component: undefined,
      messages: this.$epb.messages.insights.analytics.kpis,
    }
  },
  computed: {
    initialSelectedKpis(): KpiEnum[] {
      return this.kpisItems.map(kpiLegendItem => kpiLegendItem.id)
    },
    kpisItems(): ValueConfig[] {
      return this.supportedKpis.reduce((kpisItems: ValueConfig[], kpi: KpiEnum) => {
        const kpiMessage = {
          [KpiEnum.NO_RESULTS]: () => this.messages.noResults,
          default: () => this.messages[kpi],
        }
        const kpiText = (kpiMessage[kpi] || kpiMessage.default)().valueLabel

        kpisItems.push({
          id: kpi,
          label: kpiText,
          unfilled: true,
        })
        return kpisItems
      }, [])
    },
    period(): { startDate: Date; endDate: Date } {
      const now = new Date()

      return {
        startDate: new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 7)),
        endDate: new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())),
      }
    },
    areaChartParams(): Params<EvolutionChartConfig, EvolutionChartData> {
      return {
        config: {
          filteredValues: this.kpisItems.filter(item => {
            return this.selectedKpis.includes(item.id)
          }),
        },
        data: this.areaChartData,
      }
    },
    areaChartData(): EvolutionChartData[] {
      const dist = this.category ? this.data?.dist : this.data?.distributionData

      if (!dist) {
        return []
      }

      return dist.map(
        (distributionDataItem: DailyByCategoryDistData | TimelySearchDistributionData) => {
          const date = this.category
            ? (distributionDataItem as DailyByCategoryDistData).date
            : (distributionDataItem as TimelySearchDistributionData).datetime
          return {
            id: date.toISOString(),
            labels: this.getAreaChartDataItemLabels(date),
            values: this.getAreaChartDataItemValues(distributionDataItem, !!this.category),
          }
        },
      )
    },
  },
  watch: {
    areaChartParams: {
      handler(params: Params<EvolutionChartConfig, EvolutionChartData>): void {
        this.component.update(params)
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new EvolutionChartComponent()
  },
  mounted(): void {
    this.selectedKpis = this.initialSelectedKpis
    this.component.init(this.$refs.component, this.areaChartParams)
  },
  beforeDestroy(): void {
    this.component.destroy()
  },
  methods: {
    getAreaChartDataItemLabels(date: Date): string[] {
      const dateLocaleOptions = {
        month: 'short',
        day: 'numeric',
      }
      const dateWithoutOffset = this.getDateWithoutTimezone(date)
      return [dateWithoutOffset.toLocaleString(this.$epb.locale, dateLocaleOptions)]
    },
    getDateWithoutTimezone(date: Date): Date {
      return new Date(date.getTime() + date.getTimezoneOffset() * (60 * 1000))
    },
    getAreaChartDataItemValues(
      distributionDataItem: TimelySearchDistributionData | DailyByCategoryDistData,
      isCategoryFiltered: boolean,
    ): Partial<Record<KpiEnum, [number]>> {
      const values: Partial<Record<KpiEnum, [number]>> = {}

      this.supportedKpis.forEach((kpi: KpiEnum) => {
        const getKpiValueFn = this.getKpiValueFn(kpi, isCategoryFiltered)
        values[kpi] = [getKpiValueFn(distributionDataItem)]
      })

      return values
    },
    getKpiValueFn(
      kpi: KpiEnum,
      isCategoryFiltered: boolean,
    ): (data: TimelySearchDistributionData | DailyByCategoryDistData) => number {
      return {
        [KpiEnum.QUERIES]: (data: TimelySearchDistributionData | DailyByCategoryDistData) =>
          data.queryCount,
        [KpiEnum.CLICKS]: (data: TimelySearchDistributionData | DailyByCategoryDistData) =>
          data.clickCount,
        [KpiEnum.FINDABILITY]: (data: TimelySearchDistributionData | DailyByCategoryDistData) =>
          data.successCount,
        [KpiEnum.NO_RESULTS]: (data: TimelySearchDistributionData | DailyByCategoryDistData) =>
          isCategoryFiltered ? 0 : (data as TimelySearchDistributionData).noResultsCount,
      }[kpi]
    },
    async fetchData(): Promise<TimelySearchDistributionData | DailyByCategoryData> {
      const params = {
        ...(this.category ? { keywords: this.selectedId } : { terms: this.selectedId }),
        ...this.period,
        ...(this.filters && {
          filters: this.filters,
        }),
        ...(this.category && {
          categoryId: this.category.id,
          level: this.category.level,
          ...(this.category.level > 1 && { parentId: this.category.parentId }),
        }),
      }

      const response = await this.fetchStatsReport({
        id: this.category
          ? StatsReportsIdsEnum.DAILY_QUERIES_BY_CATEGORY
          : StatsReportsIdsEnum.DAILY_QUERIES,
        params,
      })
      return response.data
    },
  },
})
